import { useState, useCallback, useEffect } from 'react';
import axiosInstance from 'api/axiosInstance';
import useNotificationProvider from 'hooks/notificationBar/useNotificationProvider';

export interface Attachment {
  Id: number;
  FileName: string;
  Description: string;
  FileSize: number;
  UploadedBy: string;
  UploadedOn: string;
  Url: string;
}

function useCaseAttachments(caseId: number, secure?: boolean) {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Attachment | null>(null);
  const { notifySuccess, notifyError } = useNotificationProvider();

  const fetchAttachments = useCallback(async () => {
    setLoading(true);
    try {
      const secureParam = secure ? '&secure=true' : '';
      const response = await axiosInstance.get(
        `/api/cantrackv3/ReadAttachments?caseId=${caseId}${secureParam}`
      );
      if (response?.data) {
        setAttachments(Array.isArray(response.data) ? response.data : []);
      }
    } catch (err) {
      console.error('[CaseAttachments] Error fetching attachments:', err);
      notifyError('Failed to load attachments');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [caseId, secure, notifyError]);

  useEffect(() => {
    if (caseId) {
      fetchAttachments();
    }
  }, [caseId, fetchAttachments]);

  const deleteAttachment = useCallback(async (attachmentId: number) => {
    try {
      await axiosInstance.post(
        `/api/fileuploadservice/Delete?FileId=${attachmentId}`,
        { fileId: attachmentId },
        { headers: { 'Content-Type': 'application/json;charset=UTF-8' } }
      );
      notifySuccess('Attachment deleted successfully');
      await fetchAttachments();
    } catch (err) {
      console.error('[CaseAttachments] Error deleting attachment:', err);
      notifyError('Failed to delete attachment');
      throw err;
    }
  }, [fetchAttachments, notifySuccess, notifyError]);

  const openUploadModal = useCallback(() => setUploadModalOpen(true), []);
  const closeUploadModal = useCallback(() => setUploadModalOpen(false), []);

  const onUploadSuccess = useCallback(() => {
    fetchAttachments();
  }, [fetchAttachments]);

  const openDeleteDialog = useCallback((attachment: Attachment) => {
    setDeleteTarget(attachment);
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setDeleteTarget(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (deleteTarget) {
      await deleteAttachment(deleteTarget.Id);
      setDeleteTarget(null);
    }
  }, [deleteTarget, deleteAttachment]);

  const state = { attachments, loading, uploadModalOpen, deleteTarget };
  const handler = {
    fetchAttachments,
    deleteAttachment,
    openUploadModal,
    closeUploadModal,
    onUploadSuccess,
    openDeleteDialog,
    closeDeleteDialog,
    confirmDelete,
  };

  return { state, handler };
}

export default useCaseAttachments;
